import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { api } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff8042', '#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const statusColors = {
  inquiry: '#8884d8',
  box_collection: '#82ca9d',
  box_collection_done: '#ffc658',
  box_delivery: '#ff8042',
  box_delivery_done: '#0088FE',
  quick_packing: '#00C49F',
  quick_packing_done: '#FFBB28',
  shipment: '#FF8042',
  delivered: '#00C49F',
  pending: '#8884d8',
};

const Reports = () => {
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError('');
      try {
        const res = await api.get('/reports/overview', {
          params: {
            startDate: startDate.toISOString().slice(0, 10),
            endDate: endDate.toISOString().slice(0, 10),
          },
        });
        setData(res.data);
      } catch (err) {
        setError('Failed to load report data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate]);

  // Prepare data for charts
  const dailyTotalsData = React.useMemo(() => {
    if (!data?.dailyTotals) return [];
    // Group by date, then by status
    const grouped = {};
    data.dailyTotals.forEach(row => {
      if (!grouped[row.date]) grouped[row.date] = {};
      grouped[row.date][row.status] = Number(row.count);
    });
    // Build array for recharts
    return Object.entries(grouped).map(([date, statuses]) => ({
      date: date,
      ...statuses,
    }));
  }, [data]);

  // Dynamically get all statuses present in the data
  const allStatuses = React.useMemo(() => {
    if (!data?.dailyTotals) return [];
    const set = new Set();
    data.dailyTotals.forEach(row => set.add(row.status));
    return Array.from(set);
  }, [data]);

  // Map null user_name to 'Unassigned' for jobsByCreator
  const jobsByCreatorData = React.useMemo(() => {
    if (!data?.jobsByCreator) return [];
    return data.jobsByCreator.map(item => ({
      ...item,
      user_name: item.user_name || 'Unassigned',
      count: Number(item.count),
    }));
  }, [data]);

  // Map null assignee_name to 'Unassigned' for jobsByAssignee
  const jobsByAssigneeData = React.useMemo(() => {
    if (!data?.jobsByAssignee) return [];
    return data.jobsByAssignee.map(item => ({
      ...item,
      assignee_name: item.assignee_name || 'Unassigned',
      count: Number(item.count),
    }));
  }, [data]);

  return (
    <div className="space-y-6 p-4 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Reports</h1>
      <div className="flex flex-col sm:flex-row gap-4 items-center mb-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Date Range:</span>
          <DatePicker
            selected={startDate}
            onChange={date => setStartDate(date)}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            maxDate={endDate}
            dateFormat="yyyy-MM-dd"
            className="border px-2 py-1 rounded"
          />
          <span>-</span>
          <DatePicker
            selected={endDate}
            onChange={date => setEndDate(date)}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            dateFormat="yyyy-MM-dd"
            className="border px-2 py-1 rounded"
          />
        </div>
      </div>
      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="text-red-600">{error}</div>
      ) : data ? (
        <div className="space-y-8">
          {/* 1. Bar Chart: Daily totals with status breakdown */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Daily Jobs Created (by Status)</h2>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={dailyTotalsData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="date" tickFormatter={date => date.slice(0, 10)} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend payload={allStatuses.map(status => ({
                  value: status,
                  type: 'rect',
                  color: statusColors[status] || '#ccc',
                }))} />
                {allStatuses.map(status => (
                  <Bar key={status} dataKey={status} stackId="a" fill={statusColors[status] || '#ccc'} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 2. Pie Chart: Jobs created by user */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Jobs Created by User</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={jobsByCreatorData}
                    dataKey="count"
                    nameKey="user_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {jobsByCreatorData.map((entry, idx) => (
                      <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold mb-4">Jobs by Assignee</h2>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={jobsByAssigneeData}
                    dataKey="count"
                    nameKey="assignee_name"
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    label
                  >
                    {jobsByAssigneeData.map((entry, idx) => (
                      <Cell key={`cell2-${idx}`} fill={COLORS[idx % COLORS.length]} />
                    ))}
                  </Pie>
                  <Legend />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* 4. Job Aging Chart */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Job Aging</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.jobAging} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <XAxis dataKey="bucket" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" fill="#8884d8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Reports; 